import { useState, useEffect, FormEvent } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../utils/supabase";
import { useLoadingState } from "../hooks/useLoadingState";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useToast } from "../contexts/ToastContext";
import { ProfileSkeleton } from "../components/Skeleton";
import { ToastSettings } from "../components/ToastSettings";
import { useKeyboardShortcut } from "../hooks/useKeyboardShortcut";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const container = {
    hidden: { opacity: 0, y: 20 },
    show: { 
        opacity: 1, 
        y: 0,
        transition: {
            duration: 0.3,
            ease: "easeOut"
        }
    }
};

export default function Profile() {
    const { profile, user } = useAuth();
    const navigate = useNavigate();
    const [fullName, setFullName] = useState(profile?.full_name || "");
    const { isLoading, startLoading, stopLoading, setError } = useLoadingState();
    const { showToast } = useToast();

    useEffect(() => {
        if (profile) {
            setFullName(profile.full_name || "");
        }
    }, [profile]);

    useKeyboardShortcut({
        s: (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                updateProfile();
            }
        },
        escape: () => {
            navigate(-1);
        },
    });

    async function updateProfile(e?: FormEvent) {
        if (e) {
            e.preventDefault();
        }
        if (!user) return;

        startLoading("Saving changes...");

        try {
            const { error } = await supabase
                .from("profiles")
                .update({
                    full_name: fullName,
                    updated_at: new Date().toISOString(),
                })
                .eq("id", user.id);

            if (error) throw error;
            showToast("Profile updated successfully", "success");
        } catch (error) {
            setError(error instanceof Error ? error : new Error("Failed to update profile"));
            showToast("Failed to update profile", "error");
        } finally {
            stopLoading();
        }
    }

    return (
        <div className="relative">
            <div className="max-w-xl mx-auto">
                <h1>Profile Settings</h1>
                {isLoading ? (
                    <ProfileSkeleton />
                ) : (
                    <motion.div
                        variants={container}
                        initial="hidden"
                        animate="show"
                        className="mt-8"
                    >
                        <div className="space-y-8">
                            <div className="card">
                                <h2 className="text-xl font-semibold mb-6">Account Settings</h2>
                                <form onSubmit={updateProfile} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Email
                                    </label>
                                    <input
                                        aria-label="Email address"
                                        type="email"
                                        disabled
                                        value={profile?.email || ""}
                                        className="input bg-gray-50 dark:bg-gray-800 text-text-secondary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Full Name
                                    </label>
                                    <input
                                        aria-label="Full name"
                                        type="text"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        className="input"
                                        placeholder="Enter your full name"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="btn btn-primary w-full"
                                    aria-label={isLoading ? "Saving changes..." : "Save changes"}
                                >
                                    {isLoading ? "Saving..." : "Save Changes"}
                                </button>
                                <div className="text-xs text-text-secondary text-center space-y-2">
                                    <p className="flex items-center justify-center space-x-2">
                                        <span>Press</span>
                                        <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md inline-flex items-center space-x-1">
                                            <span className="text-xs">⌘</span>
                                            <span>+</span>
                                            <span>S</span>
                                        </kbd>
                                        <span>to save</span>
                                    </p>
                                    <p className="flex items-center justify-center space-x-2">
                                        <span>Press</span>
                                        <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md">
                                            Esc
                                        </kbd>
                                        <span>to go back</span>
                                    </p>
                                </div>
                                </form>
                            </div>

                            <div className="card">
                                <h2 className="text-xl font-semibold mb-6">Notification Settings</h2>
                                <ToastSettings />
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
